'use client';

import { createContext, useContext, ReactNode, useEffect, useState } from 'react';
import { useWorkspaces } from '../../hooks/use-workspaces';
import { useCurrentWorkspace } from '../../hooks/use-current-workspace';
import type { Workspace } from '../api/types';
import { useUser } from '@/hooks/use-user';

export interface WorkspaceContextValue {
  activeWorkspaceId: string | null;
  setActiveWorkspaceId: (workspaceId: string | null) => Promise<void>;
  isLoading: boolean;
}

const WorkspaceContext = createContext<WorkspaceContextValue | null>(null);

interface WorkspaceProviderProps {
  children: ReactNode;
}

export function WorkspaceProvider({ children }: WorkspaceProviderProps) {
  const { user } = useUser();
  const { data: workspaces, isLoading: workspacesLoading, mutate: mutateWorkspaces } = useWorkspaces();
  const { currentWorkspace, isLoading: currentWorkspaceLoading, mutate: mutateCurrentWorkspace } = useCurrentWorkspace();
  const [activeWorkspaceId, setActiveWorkspaceId] = useState<string | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);

  // Initialize from current workspace API when it loads
  useEffect(() => {
    if (user && currentWorkspace && !isInitialized) {
      setActiveWorkspaceId(currentWorkspace.id);
      setIsInitialized(true);
    } else if (!user) {
      setActiveWorkspaceId(null);
      setIsInitialized(false);
    }
  }, [user, currentWorkspace, isInitialized]);

  const setActiveWorkspaceIdWithDatabase = async (workspaceId: string | null) => {
    if (!workspaceId) {
      console.warn('Cannot set null workspace ID');
      return;
    }

    // Update local state immediately for responsive UI
    setActiveWorkspaceId(workspaceId);
    
    // Update database in background
    try {
      const response = await fetch(`/api/workspaces/${workspaceId}/set-current`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to set current workspace');
      }

      // Refetch both workspaces and current workspace to update state
      await Promise.all([
        mutateWorkspaces(),
        mutateCurrentWorkspace()
      ]);
    } catch (error) {
      console.error('Failed to update current workspace in database:', error);
      
      // Revert local state on error
      setActiveWorkspaceId(currentWorkspace?.id || null);
      
      throw error;
    }
  };

  // Auto-select first workspace if none selected and user has workspaces
  useEffect(() => {
    if (!workspacesLoading && !currentWorkspaceLoading && workspaces && workspaces.length > 0 && !currentWorkspace && isInitialized) {
      // If no current workspace, set the first one as current
      setActiveWorkspaceIdWithDatabase(workspaces[0].id).catch(error => {
        console.error('Failed to auto-select workspace:', error);
      });
    }
  }, [workspaces, workspacesLoading, currentWorkspaceLoading, currentWorkspace, isInitialized]);

  // Clear selection if the selected workspace no longer exists
  useEffect(() => {
    if (!workspacesLoading && workspaces && activeWorkspaceId && isInitialized) {
      const workspaceExists = workspaces.some((workspace: Workspace) => workspace.id === activeWorkspaceId);
      if (!workspaceExists) {
        setActiveWorkspaceId(null);
      }
    }
  }, [workspaces, workspacesLoading, activeWorkspaceId, isInitialized]);

  const value: WorkspaceContextValue = {
    activeWorkspaceId,
    setActiveWorkspaceId: setActiveWorkspaceIdWithDatabase,
    // Only show loading on initial load when there's no data yet
    isLoading: (workspacesLoading || currentWorkspaceLoading) && !isInitialized,
  };

  return (
    <WorkspaceContext.Provider value={value}>
      {children}
    </WorkspaceContext.Provider>
  );
}

export function useWorkspaceContext(): WorkspaceContextValue {
  const context = useContext(WorkspaceContext);
  if (!context) {
    throw new Error('useWorkspaceContext must be used within a WorkspaceProvider');
  }
  return context;
} 